import { useMemo } from "react";
import { ConnectProto } from "@racoon/api";
import { fromTimestamp } from "@racoon/lib";
import { DateValue } from "~/shared/lib/analytics-utils";
import { byDateThenPickupLocation, containerVolume } from "./job-report";
import { ReportJob, useReportJobs } from "./use-report-data";
import { useReportFilterValues } from "./use-report-filters";

/** One row of «Заявки» (AIS 15.1.1). */
export interface RequestReportRow {
  jobId: bigint;
  twStartAt: Date | null;
  serviceReceiverId: bigint | null;
  serviceReceiverName: string;
  serviceReceiverBin: string;
  pickupLocationId: bigint | null;
  pickupLocationName: string;
  containerTypeId: string;
  containerTypeName: string;
  numberOfContainers: number | null;
  containerVolume: number | null;
  factTotalVolume: number | null;
  /** Short JobStatus name, e.g. "COMPLETED". */
  status: string;
  createdAt: Date | null;
  updatedAt: Date | null;
}

// Every status: the report lists requests, not only completed work.
const ALL_STATUSES: ConnectProto.JobStatus[] = [];

const toRow = (job: ReportJob): RequestReportRow => ({
  jobId: job.id,
  twStartAt: fromTimestamp(job.twStartAt),
  serviceReceiverId: job.serviceReceiverId,
  serviceReceiverName: job.serviceReceiverName ?? "",
  serviceReceiverBin: job.serviceReceiverBin ?? "",
  pickupLocationId: job.pickupLocationId,
  pickupLocationName: job.pickupLocationName ?? "",
  containerTypeId: job.containerTypeId ? String(job.containerTypeId) : "",
  containerTypeName: job.containerTypeName ?? "",
  numberOfContainers: job.numberOfContainers ?? null,
  containerVolume: containerVolume(job),
  factTotalVolume: job.factTotalVolume ?? null,
  status: ConnectProto.JobStatus[job.status] ?? "UNDEFINED",
  createdAt: fromTimestamp(job.createdAt),
  updatedAt: fromTimestamp(job.updatedAt),
});

/** Jobs of any status whose service window starts in the period. */
export function useRequestReportRows(startDate: DateValue, endDate: DateValue) {
  const { serviceReceiver, pickupLocation, containerType } = useReportFilterValues();
  const { rows, isLoading, error } = useReportJobs(startDate, endDate, ALL_STATUSES, {
    serviceReceiver,
    pickupLocation,
  });

  const requestRows = useMemo(
    () =>
      rows
        .map(toRow)
        .filter((row) => !containerType || row.containerTypeId === containerType)
        .sort(byDateThenPickupLocation((row) => row.twStartAt)),
    [rows, containerType],
  );

  return { rows: requestRows, isLoading, error };
}
